/**
 * Lighweight but powerfull CSV string parser for browsers
 * @module
 * @desc
 * 		Provides a complete CSV parser allowing to validate, tokenize, type check and cast a string holding CSV data 
 * 		for conversion into an in-memory array of arrays or objects. With default settings, the parser is fully
 *      compliant with IEFT RFC4180, with minor additions:
 * 			* Empty lines are ignored
 * 			* Lines starting with # are ignored (recommended for adding comments like title, sources, copyrigths in CSV)
 * 
 * 		The parser also natively support, in addition to the standard optional header row, an additional optional 
 * 		second header row for types definition, allowing custom casting of data values to primitive types (string, int,
 * 		float and boolean). Headers and type casting can also be provided or overridden by the caller.
 * 
 * 		CSVError are thrown for any non-compliant input, and a custom error handler could be provided by the caller.
 * 		Ignoring errors (not throwing them) in that handler is strongly discouraged, since the behaviour of the parser
 * 		after such an error is not fully determined, especially when the error is related to delimiters pairing.
 */

import {set} from './nested-property-helpers.js';
import {regexEscape, createCustomError} from './helpers.js';

/**
 * Parser options.
 * @typedef {Object} ParserOptions
 * @property {string} separator - A single character string used to split CSV rows into columns (default to comma for IETF RFC4180 compliance).
 * @property {string} delimiter - A single character string used to wrap CSV values (default to double quote for IETF RFC4180 compliance).
 * @property {boolean} headers - Should the first data line be interpreted as a header line according to IETF RFC4180 ? (default to true).
 * @property {boolean} types - Should the second data line be interpreted as a types line ? (default to false for IETF RFC4180 compliance).
 * @property {RegExp} tokenizer - The regular expression used internaly by the parser for parsing and tokenization, only change the default if you know what you are doing!
 * @property {RegExp} falsy - The regular expression used to evaluate falseness during casting to boolean.
 */

/**
 * Accumulator.
 * @typedef {Array|Object} Accumalator
 * @desc
 * 		The accumalator is the Array or Object that will collect the result of the parsing.
 */  

/**
 * Headers.
 * @typedef {string[]|Array} Headers
 * @desc
 * 		An array containing the column names of the currently parsed file.
 */  

/**
 * Types.
 * @typedef {string[]|Array} Types
 * @desc
 * 		An array containing the data types for each columns of the currently parsed file.
 */  

/**
 * Record.
 * @typedef {Object|Array} Record
 * @desc
 * 		The accumulator for a single record. 
 */

 /**
 * Values.
 * @typedef {Object} Values
 * @property {string} string - The string value read from input and properly unescaped.
 * @property {number} int - The integer value parsed from the above string, when that string looks like an interger (no decimal point read).
 * @property {number} float - The float value parsed form the above string, when that string looks like a float (decimal point read) or an integer.
 * @property {boolean} boolean - The boolean value of the string above, according to the `falsy` expression in {@link ParserOptions}.
 * @desc
 * 		The accumulator for a single record. 
 */

/**
  * Init handler.
  * @typedef {function} InitHandler
  * @this Accumulator
  * @param {string} Input string to be parsed.
  * @returns {Accumulator} The {@link Accumlator} that will be provided to other handler as their this keyword.
  * @throws {CSVError} when any fatal error prevent the parsing to be pursued.
  * @desc
  *   The init handler is called as soon as the first element have been able to be tokenized and before any processing take place.
  *   In the init handler, the this keyword refers to the {@link Accumulator} that have been provided to and that will be returned by
  *   the {@link parse} function. This handler should either returns that same {@link Accumulator}, or ensure in cooperation with the
  *   {@link DoneHandler} that the received accumlator will be fed with the final result. The {@link Accumulator} returned by this
  *   handler will be the this keyword for any other handlers.
  *   If this handler is not provided, it is equivalent to `return this;`.
  */ 

  /**
  * Headers handler.
  * @typedef {function} HeadersHandler
  * @this Accumulator
  * @param {Headers} Headers read from input, or null when `headers` is `false` in {@link ParserOptions}.
  * @param {number} Count of fields read from the header row, or 0 when `headers` is `false` in {@link ParserOptions}.
  * @returns {Headers} The headers that will be provided to other handlers.
  * @throws {CSVError} when any fatal error prevent the parsing to be pursued.
  * @desc
  * 	The headers handler is called just after the {@link StartHandler} when `headers` is `false` in {@link ParserOptions}, or
  * 	just after the first row has been parsed otherwise. It received the read headers when available, and it may returns
  * 	the {@link Headers} that will replace the read ones. The {@link Headers} will be provided AS IS to later handlers.
  * 	When `headers` is `false` in {@link ParserOptions} and this handler returns a non-empty array, the length of that
  * 	array will be considered for the expected count of field in each records.
  */

 /**
  * Types handler.
  * @typedef {function} TypesHandler
  * @this Accumulator
  * @param {Headers} Headers read from input, or returned by the {@link HeadersHandler}.
  * @param {Types} Data types declaration read from the second row of the input, or null when `types` is `false` in {@link ParserOptions}.
  * @param {number} Count of fields read from the first row or from the {@link Headers} provided by the {@link HeadersHandler}.
  * @returns {Types} The data types that will be provided to other handlers.
  * @throws {CSVError} when any fatal error prevent the parsing to be pursued.
  * @desc
  * 	The types handler is called just after the {@link HeadersHandler} when `types` is `false` in {@link ParserOptions}, or
  * 	just after the second row has been parsed otherwise. It received the read data types when available, and it may returns
  * 	the {@link Types} that will replace the read ones. The {@link Types} will be provided AS IS to later handlers.
  * 	The length of the returned array must match the count of fields provided.
  * 	This handler is never called if `headers` is `false` in {@link ParserOptions} and no {@link Headers} was provided by the {@link HeadersHandler}.
  */

 /**
  * Start handler.
  * @typedef {function} StartHandler
  * @this Accumulator
  * @param {Headers} Headers read from input, or returned by the {@link HeadersHandler}.
  * @param {Types} Data types declaration read from the second row of the input, or null when `types` is `false` in {@link ParserOptions}.
  * @param {number} Count of fields read from the first row or from the {@link Headers} provided by the {@link HeadersHandler}.
  * @returns {Accumulator} The {@link Accumlator} that will be provided to later handler as their this keyword.
  * @throws {CSVError} when any fatal error prevent the parsing to be pursued.
  * @desc
  *   The start handler is called before proceeding to the parsing of the data, and after headers and types has been processed.
  *   In the start handler, the this keyword refers to the {@link Accumulator} that have been provided to and that will be returned by
  *   the {@link parse} function, or the one that the {@link InitHandler} has returned. This handler should either returns that same 
  *   {@link Accumulator}, or ensure in cooperation with the {@link DoneHandler} that the received accumlator will be fed with the final result. 
  *   The {@link Accumulator} returned by this handler will be the this keyword for later handlers.
  *   If this handler is not provided, it is equivalent to `return this;`.
  */ 

 /**
  * New record handler.
  * @typedef {function} NewRecordHandler
  * @this Accumulator
  * @param {Headers} Headers read from input, or returned by the {@link HeadersHandler}.
  * @param {Types} Data types declaration read from the second row of the input, or null when `types` is `false` in {@link ParserOptions}.
  * @param {number} Current record number, starting at 1 with the first data record.
  * @param {number} Count of fields expected in the upcoming data record or 0 when reading the first record and `headers` is `false` in {@link ParserOptions} 
  * 				and no {@link Headers} was provided by the {@link HeadersHandler}.
  * @returns {Record} Optionally, an empty record that will be provided to later handlers for filling.
  * @throws {CSVError} when any fatal error prevent the parsing to be pursued.
  * @desc
  * 	The new record handler is called for each records, before reading the new record from input. The record number received is the one of
  *     the record that is about to be read. When the count of fields is 0, while the record number is 1, it means that nothing has been read
  * 	yet, and that the first record will determine the count of fields for all later records.
  * 	The new record handler may return an empty {@link Record} that will be provided to later handler to accumulate record values.
  */

/**
  * Value handler.
  * @typedef {function} ValueHandler
  * @this Accumulator
  * @param {Record} The current {@link Record} accumulator.
  * @param {Values} The value read from input.
  * @param {number} The current field index (starting at 0) of the value read.
  * @param {Headers} Headers read from input, or returned by the {@link HeadersHandler}.
  * @param {Types} Data types declaration read from the second row of the input, or returned by the {@link TypesHandler}.
  * @param {number} Current record number, starting at 1 with the first data record.
  * @param {number} Count of fields expected in the current data record or 0 when reading the first record and `headers` is `false` in {@link ParserOptions} 
  * 				and no {@link Headers} was provided by the {@link HeadersHandler}.
  * @throws {CSVError} when any fatal error prevent the parsing to be pursued.
  * @desc
  * 	The value handler is called after reading each value of a record. It received the read value fully unescaped but unchanged otherwise,
  * 	as well as typed version of that value that is appropriate according to the value read. It should process the value read, and it could
  * 	do so by filling the current {@link Record} accumulator (if one was provided by the {@link NewRecordHandler}) or the {@link Accumulator} itself.
  * 	Without any processing here, the parser will not store any result read, and will just validate the input file to be IETF RFC4180 compliant.
  */

  /**
  * Record handler.
  * @typedef {function} RecordHandler
  * @this Accumulator
  * @param {Record} The current {@link Record} accumulator.
  * @param {Headers} Headers read from input, or returned by the {@link HeadersHandler}.
  * @param {Types} Data types declaration read from the second row of the input, or returned by the {@link TypesHandler}.
  * @param {number} Current record number, starting at 1 with the first data record.
  * @param {number} Count of fields expected in the following data record.
  * @returns {Accumulator} Optionaly, the {@link Accumlator} that will be provided to later handler calls as their this keyword.
  * @throws {CSVError} when any fatal error prevent the parsing to be pursued.
  * @desc
  * 	The record handler is called for each record read, after the record has been fully read. It received the current {@link Record} accumulator 
  * 	(if one was provided by the {@link NewRecordHandler}), and it is expected to transfert that record into the {@link Accumulator}.
  * 	Without any processing here, unless the {@link ValueHandler} has directly feed the {@link Accumulator}, the parser will not store any result read,
  * 	and will just validate the input file to be IETF RFC4180 compliant.
  * 	This handler can replace the current {@link Accumlator} by a new one, if it returns a not null or undefined value. Remember that the original
  * 	{@link Accumlator} is the value that will be returned by the parser, and that replacing it here will need to be handle in some ways.
  */

  /**
  * Done handler.
  * @typedef {function} DoneHandler
  * @this Accumulator
  * @param {Headers} Headers read from input, or returned by the {@link HeadersHandler}.
  * @param {Types} Data types declaration read from the second row of the input, or returned by the {@link TypesHandler}.
  * @param {number} Last record number, which represent the count of data records read.
  * @param {number} Count of fields found in those records.
  * @throws {CSVError} when any fatal error prevent the parsing to be pursued.
  * @desc
  * 	The done handler is called at the very end of the parsing, after the last call to the {@link RecordHandler}. It received the current
  * 	{@link Accumlator} and it may provides some trickery for the original {@link Accumlator} to receive an appropriate content, if any 
  * 	of the {@link StartHandler} or {@link RecordHandler} have replaced the original {@link Accumlator}.
  */

  /**
  * Error handler.
  * @typedef {function} ErrorHandler
  * @this Accumulator
  * @param {errorCode} The code of the error.
  * @param {Object} An object holding information about the error.
  * @throws {CSVError} with the above error information.
  * @desc
  * 	The error handler is called on any error by the parser and should throw a {@link CSVError}. The information received from the
  * 	parser depends on the error raised.
  */

 /**
  * Parser handlers used to handle and convert the information read into any other structure. 
  * @typedef {Object} ParserHandlers
  * @property {InitHandler} init
  * @property {HeadersHandler} headers
  * @property {TypesHandler} types
  * @property {StartHandler} start
  * @property {NewRecordHandler} newRecord
  * @property {ValueHandler} value
  * @property {RecordHandler} record
  * @property {DoneHandler} done
  * @property {ErrorHandler} error
  */

export var tokenizers = {
    // IETF RFC4180 compliance with support for empty lines and comments
    default: 
      /(?:(,)|(?:(?:\r?\n|\r|^)(?:#[^\r\n]*)?)+)(?:$|(")([^"]*(?:("")[^"]*)*)"|(-?[0-9]*\.[0-9]*)|(-?[0-9]+)|([^",\r\n]*))?([^",\r\n]*)([^,\r\n]*)/g,
    // IETF RFC4180 compliance with support comments
    CommentedRFC4180: 
      /(?:(,)|(?:\r?\n|\r|^)(?:#[^\r\n]*(?:\r?\n|\r))*)(?:$|(")([^"]*(?:("")[^"]*)*)"|(-?[0-9]*\.[0-9]*)|(-?[0-9]+)|([^",\r\n]*))?([^",\r\n]*)([^,\r\n]*)/g,
    // Strict compliance to IETF RFC4180
    pureRFC4180:
      /(?:(,)|(?:\r?\n|\r|^))(?:$|(")([^"]*(?:("")[^"]*)*)"|(-?[0-9]*\.[0-9]*)|(-?[0-9]+)|([^",\r\n]*))?([^",\r\n]*)([^,\r\n]*)/g,
    // Very relaxed CSV syntax, ignoring comments, empty lines, and allowing single and double quotes as delimiters
    flexCSV: 
      /(?:(,)|(?:(?:\r?\n|\r|^)(?:#[^\r\n]*)?)+)(?:$|(["'])((?:(?!\2)(?:.|\r?\n|\r))*(?:(\2\2)(?:(?!\2)(?:.|\r?\n|\r))*)*)\2|(-?[0-9]*\.[0-9]*)|(-?[0-9]+)|([^,\r\n]*))?([^,\r\n]*)([^,\r\n]*)/g
}

/**
 * Defaults for parser options.
 * @type {ParserOptions}
 */ 
export var defaults = {
	separator: ',',
    delimiter: '"',
    headers: true,
    types: false,
    tokenizer: tokenizers.default,
    falsy:
    	/^\s*(false|-?0+|off|null|undefined|NaN)?\s*$/i
};

/**
 * Error codes for the error handlers, and trown CSVParser errors.
 * @readonly
 * @enum {number}
 */
export var errorCodes =  Object.freeze({
	INCOMPLETE_RECORD:1, 
	EXCEEDING_FIELD:2, 
	UNEXPECTED_CHAR:3,
	TYPE_MISMATCH:4
});

/**
 * Raw CSV string parser.
 * @param {string} input - The string to be parsed.
 * @param {ParserOptions} options - Overrides over the {@link defaults} options for the parser.
 * @param {ParserHandlers} handlers to be used during parsing to collect the read data.
 * @param {Accumulator} accumalator - An array or object that will be used to accumulate the read data.
 * @returns {Accumulator} The provided accumulator.
 * @desc
 * 		This the raw parser method that could be use to create new kind of parsers based on any handlers.
 */
export function parse(input, options, handlers, accumulator) {
	var tokenizer = getOption(options,'tokenizer', defaults),
		falsy = getOption(options,'falsy', defaults),
		separator = getOption(options,'separator', defaults),
		delimiter = getOption(options,'delimiter', defaults),
		withHeaders = getOption(options,'headers', defaults),
		withTypes = getOption(options,'types', defaults),
		defaultHandlers = defaults.defaultHandlers,
    initHandler = getOption(handlers, 'init'),
		headersHandler = getOption(handlers, 'headers'),
		typesHandler = getOption(handlers, 'types'),
    startHandler = getOption(handlers, 'start'),
		newRecordHandler = getOption(handlers, 'newRecord'),
		valueHandler = getOption(handlers, 'value'),
		recordHandler = getOption(handlers, 'record'),
		doneHandler = getOption(handlers, 'done'),
		errorHandler = getOption(handlers, 'error') || defaultErrorHandler;

  // Override delimiter and separator
	if (options 
		&& tokenizer === defaults.tokenizer 
		&& (delimiter != defaults.delimiter
		    || separator != defaults.separator)) {
		tokenizer = new RegExp(tokenizer.source.replace(/,/g, regexEscape(separator))
											   .replace(/"/g, regexEscape(delimiter)), 'g');
	}
	
	input.replace(tokenizer, 
		getParser(withHeaders, withTypes, falsy, 
			initHandler, headersHandler, typesHandler, startHandler, newRecordHandler, valueHandler, recordHandler, doneHandler, errorHandler, 
			accumulator));

	return accumulator;
}

/**
 * Handlers to produce array of arrays.
 * @param {Types} typesOverride - Optional, an array to override or declare the data types to be read for each columns.
 * @param {ErrorHandler} options - Optional, a error handler to replace the default one.
 * @returns {ParserHandlers} the parser handlers that produce an array of arrays.
 */
export function arrayHandlers(typesOverride, customErrorHandler) {
	var hasTypesOverride = Array.isArray(typesOverride),
	    errorHandler = getErrorHandler(customErrorHandler);

	return {
			types: function(headers, types) { 
				return (hasTypesOverride) ? typesOverride : types;
			},
			newRecord: function() { return []; },
			value: function(rec, value, field, headers, types, record) {
				if (!types || types[field] !== 'skip') {
					rec.push(getValue.call(this, value, types, field, record, errorHandler));
				}
			},
			record: function(rec) { this.push(rec); },
			error: errorHandler || defaultErrorHandler
    	};
}

/**
 * Helper function that call the parser with the {@link arrayHandlers} handlers.
 * @param {string} input - The string to be parsed.
 * @param {ParserOptions} options - Overrides over the {@link defaults} options for the parser.
 * @param {Types} typesOverride - Optional, an array to override or declare the data types to be read for each columns.
 * @param {ErrorHandler} options - Optional, a error handler to replace the default one.
 * @returns {Accumulator} The result of the parsing, as an array of arrays.
 */
export function parseToArrays(input, options, typesOverride, customErrorHandler) {
	return parse(input, options, arrayHandlers(typesOverride, customErrorHandler), []);
}

/**
 * Handlers to produce array of arrays.
 * @param {Headers} headersOverride - Optional, an array to override or declare the headers to be used for property names.
 * @param {Types} typesOverride - Optional, an array to override or declare the data types to be read for each columns.
 * @param {ErrorHandler} options - Optional, a error handler to replace the default one.
 * @returns {ParserHandlers} the parser handlers that produce an array of objects.
 */
export function objectHandlers(headersOverride, typesOverride, customErrorHandler) {
	var hasHeaderOverride = Array.isArray(headersOverride),
		  hasTypesOverride = Array.isArray(typesOverride),
	    errorHandler = getErrorHandler(customErrorHandler);
	    
	return {
			headers: function(headers) { 
				return (hasHeaderOverride) ? headersOverride : headers || [];
			},
			types: function(headers, types) { 
				return (hasTypesOverride) ? typesOverride : types;
			},
			newRecord: function() { return {}; },
			value: function(rec, value, field, headers, types, record) {
				if (!types || types[field] !== 'skip') {
					set(rec,headers[field],getValue.call(this, value, types, field, record, errorHandler));
				} 
			},
			record: function(rec) { this.push(rec); },
			error: customErrorHandler || defaultErrorHandler
		};
}

/**
 * Helper function that call the parser with the {@link objectHandlers} handlers.
 * @param {string} input - The string to be parsed.
 * @param {ParserOptions} options - Overrides over the {@link defaults} options for the parser.
 * @param {Headers} headersOverride - Optional, an array to override or declare the headers to be used for property names.
 * @param {Types} typesOverride - Optional, an array to override or declare the data types to be read for each columns.
 * @param {ErrorHandler} options - Optional, a error handler to replace the default one.
 * @returns {Accumulator} The result of the parsing, as an array of objects.
 */
export function parseToObjects(input, options, headersOverride, typesOverride, customErrorHandler) {
	return parse(input, options, objectHandlers(headersOverride, typesOverride, customErrorHandler), []);
}

function getParser(withHeaders, withTypes, falsy, 
                   initHandler, headersHandler, typesHandler, startHandler, newRecordHandler, valueHandler, recordHandler, doneHandler, errorHandler, 
                   acc) {
	var expectedOffset = 0, 
		recordOffset = 0, 
		record = 0, 
		field = 0, 
		lastField = -1, 
		inputLength = -1,
		hrow = withHeaders ? [] : null,
		headers = null,
		types = null,
		rec = null,
    self = function (match, sep, quote, quoted, hasQuote, floatValue, intValue, strValue, text, tail, offset, input) {
  		//console.log({offset: offset, match: match, sep: sep, quoted: quoted, quote: quote, hasQuote: hasQuote, floatValue: floatValue, intValue: intValue, strValue: strValue, text: text, tail: tail});
  		if (offset == 0 && isFunction(initHandler)) {
  			acc = initHandler.call(acc, input);
  		}

  		// Parser consistency checks, should never trigger
  		if (expectedOffset != offset) {
  			throw new Error('Parser error: Unexpected ' + (offset - expectedOffset) + ' characters skipped at ' + expectedOffset + ' offset.');
  		}
  		expectedOffset = offset + match.length;

  		// Previous record ended
  		if (sep === undefined) {
  			// Either before all when no header or after reading headers
  			if (lastField<0 && ((!withHeaders && !offset) || (withHeaders && offset))) {
  				headers = hrow;
  				if (isFunction(headersHandler)) {
  					headers = headersHandler.call(acc, headers, lastField + 1) || hrow;
  					if (!offset && headers) {
  						lastField = field = headers.length - 1;
  					}
  				}
  				hrow = (withHeaders && withTypes) ? [] : null;
  			}
  			// First record ended
  			if (lastField<0 && offset) {
  				lastField = field;
  			}
  			if (lastField>=0 && ((hrow && hrow.length) || !withTypes || !withHeaders)) {
  				types = hrow;
  				if (isFunction(typesHandler)) {
  					types = typesHandler.call(acc, headers, types, lastField + 1) || hrow;
  					if (!hrow && types) {
  						field = types.length - 1;
  					}
  				}
  				hrow = null;
  			}
  			if (field < lastField) {
  				errorHandler.call(acc, errorCodes.INCOMPLETE_RECORD, {record: record, expectedFields: lastField + 1, providedFields: field + 1});
  			}
  			if (record && isFunction(recordHandler)) {
  				acc = recordHandler.call(acc, rec, headers, types, record, lastField + 1) || acc;
  			}
        if (!record && hrow == null && isFunction(startHandler)) {
            acc = startHandler.call(acc, headers, types, lastField + 1) || acc;
        }
  		}

  		// End of input
  		if (expectedOffset == ((inputLength > 0) ? inputLength : (inputLength = input.length))) {
  			// Empty ending record to be ignored
  			if (sep === undefined && quoted === undefined && intValue === undefined && floatValue === undefined && strValue === undefined && !text && !tail) {
    				if (isFunction(doneHandler)) {
    					doneHandler.call(acc, headers, types, record, lastField + 1);
    				}
    				return;
  			}
  		}

  		// New record detected
  		if (sep === undefined) {
  			recordOffset = offset;
  			if (record || hrow == null) record++;
  			field = 0;
  			if (isFunction(newRecordHandler)) {
  				rec = newRecordHandler.call(acc, headers, types, record, lastField + 1);
  			}
  		} else {
  			field++;
  			if (lastField>=0 && field > lastField) {
  				errorHandler.call(acc, errorCodes.EXCEEDING_FIELD, {record: record, expectedFields: lastField + 1});
  			}
  		}

  		if (floatValue !== undefined) {
  			strValue = (text) ? floatValue + text : floatValue;
  			floatValue = (text) ? text = undefined : parseFloat(floatValue)
  		}

  		if (intValue !== undefined) {
  			strValue = (text) ? intValue + text : intValue;
  			intValue = floatValue = (text) ? text = undefined : parseInt(intValue)
  		}

  		if (text) {
  			tail = text.concat((tail) ? tail : '');
  		}

  		// Unexpected character(s) after value
  		if (tail && (quoted !== undefined || strValue !== undefined)) {
  			errorHandler.call(acc, errorCodes.UNEXPECTED_CHAR, {
  				record: record, expectedFields: lastField + 1, 
  				currentFields: field + 1, 
  				offsetInRecord: (offset + match.length - tail.length - recordOffset), 
  				unexpectedContent: tail
  			});
  		}		

  		// Unescape string value for quoted strings
  		if (quoted !== undefined) {
  			strValue = (hasQuote) ? quoted.replaceAll(quote+quote, quote) : quoted;
  		}

  		if (!record) {
  			hrow.push(strValue || intValue || floatValue);
  		} else {
  			// console.log({record: record, field: field, recordOffset: recordOffset, offset: offset, match: match, sep: sep, quoted: quoted, floatValue: floatValue, intValue: intValue, strValue: strValue});
  			if (isFunction(valueHandler)) {
  				valueHandler.call(acc, rec, {string: strValue, int: intValue, float: floatValue, boolean: !falsy.test(strValue)}, field, headers, types, record, lastField + 1);
  			}
  		}

      // End of input, last line without end of line, callback self for last record
  		if (expectedOffset == inputLength) {
  			self('', undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, expectedOffset, input);
  		}
  	}
  return self;
}

function isFunction(fct) {
	return typeof fct === 'function';
}

function getOption(options, option, defaults) {
	return (options && options.hasOwnProperty(option) && options[option] !== undefined && options[option] != null) ? options[option] : (defaults) ? defaults[option] : undefined;
}

function getValue(value, types, field, record, errorHandler) {
	if (types) {
		var type, val;
		type = types[field];
		val = value[type];
		if (value.string && val === undefined) {
			errorHandler.call(this, errorCodes.TYPE_MISMATCH, {record: record, field: field, expectedType: type, value: value});
		}
		return val;
	}
	return value.int || value.float || value.string;
}

var CSVError = createCustomError('CSVError');

function defaultErrorHandler(code, data) {
	var error;
	data.code = code;
	switch(code) {
		case errorCodes.INCOMPLETE_RECORD:
    		error = new CSVError('Record #' + data.record + ' is incomplete, only ' + data.providedFields + ' field(s) while expecting records of ' + data.expectedFields + ' fields.', data);
    		break;
		case errorCodes.EXCEEDING_FIELD:
    		error = new CSVError('Record #' + data.record + ' contains exceeding fields, records of ' + data.expectedFields + ' fields is expected.', data);
    		break;
		case errorCodes.UNEXPECTED_CHAR:
			error = new CSVError('Unexpected character(s) [' + data.unexpectedContent + '] following a string value at offset ' + data.offsetInRecord + ' in record #' + data.record + '.', data);		
    		break;
		case errorCodes.TYPE_MISMATCH:
			error = new CSVError('Type mismatch for field ' + data.field + ' in record ' + data.record + ', expecting a ' + data.expectedType + ' value, but received value ' + JSON.stringify(data.value) + '.', data);		
    		break;
		default:
			error = new CSVError('Unknown error code #' + code, data);		
	}
	throw error;
}

function getErrorHandler(handler) {
	return isFunction(handler) ? handler : defaultErrorHandler;
}